/**
 * Say an agent — a sentence becomes a draft for the agent creator.
 *
 * Registry: agent_from_words (src/lib/ai/aiCapabilityRegistry.ts). Transport:
 * callFeatureLlm, the same client as AI query authoring (null on no provider,
 * never a throw into the screen).
 *
 * What the model sees: the person's words, the four customizable type labels,
 * summaries and examples, and the names of the workspace's values. No item
 * content (owner ruling R8, 2026-10-21: only the agent's
 * visible words reach the model later; the name and description are identity
 * only).
 *
 * What the model may return is clamped here, in the browser, by a pure
 * function: a draft is only {kind, name, description, words, valueKey}. It never
 * carries an output kind, an evidence source, an event, a review role, bulk
 * accept, the standing rule, a model or a limit — the type fixes those and the
 * database refuses any other (migration 923). A request no type can honor comes
 * back as "could not set", each with the next step instead of a rule, and the
 * sentence that asked for it is taken out of the words so nothing the agent
 * cannot hold is kept quietly. The database does not enforce this clamp; it
 * refuses mode keys as it does today.
 */

import { callFeatureLlm } from '../llmClient';
import { isAiCapabilityEnabled } from './aiAssistSettings';
import {
  AGENT_BEHAVIORS,
  CUSTOMIZABLE_BEHAVIORS,
  isAgentBehaviorKind,
  type AgentBehaviorKind,
} from './agentDefinitions';
import { DEFAULT_MODEL } from '../../../supabase/functions/_shared/modelRequest.ts';

export const AGENT_FROM_WORDS_CAPABILITY_ID = 'agent_from_words';

/** The draft the creator fills from. Nothing else may be in it. */
export interface AgentDraft {
  kind: AgentBehaviorKind;
  name: string;
  description: string;
  /** The agent's own words, in the person's voice — the editable text the agent reads. */
  words: string;
  /** Estimate type only: the value it works on, one of the workspace's values. */
  valueKey?: string;
}

/** Why a request could not be set, and the next step. */
export type CouldNotSetKind =
  /** A summary, digest or report: that is a report, not an agent. */
  | 'summary_or_report'
  /** Accepting or deciding values: that is the value's AI setting. */
  | 'accept_or_decide'
  /** Only some items: an agent works on any item it is asked about. */
  | 'only_some_items'
  /** A type the creator does not offer, or a value the workspace does not have. */
  | 'no_such_type'
  | 'no_such_value'
  | 'other';

export interface CouldNotSet {
  kind: CouldNotSetKind;
  /** What was asked, in the person's words. */
  asked: string;
  /** The next step, in plain words. */
  nextStep: string;
  /** Where the next step is, when it is a page. */
  href?: string;
}

/** A workspace value the estimate type may work on. */
export interface AgentDraftValue {
  key: string;
  label: string;
}

export interface AgentFromWordsContext {
  values: AgentDraftValue[];
  /** False when the Reports module is off for this workspace: the report next step then names the module switch. */
  reportsModuleOn?: boolean;
}

export type AgentFromWordsResult =
  | { ok: true; draft: AgentDraft | null; couldNotSet: CouldNotSet[] }
  | { ok: false; reason: string };

export interface AgentFromWordsOptions {
  /** Injectable for tests; defaults to the real callFeatureLlm. */
  callLlm?: typeof callFeatureLlm;
  /** Injectable for tests; defaults to the real workspace capability check. */
  isEnabled?: (id: string) => boolean;
  conversationId?: string;
}

const MAX_ATTEMPTS = 2;
const NAME_MAX = 60;
const DESCRIPTION_MAX = 200;
const WORDS_MAX = 4000;

// ── The next step for each thing no agent type does ───────────────────────────

const REPORTS_HREF = '/prioritization/reports';
const MODULES_HREF = '/prioritization/settings/modules';
const ATTRIBUTES_HREF = '/prioritization/settings/attributes';

export function nextStepFor(
  kind: CouldNotSetKind,
  asked: string,
  ctx?: { valueKey?: string; scope?: string; reportsModuleOn?: boolean },
): { nextStep: string; href?: string } {
  switch (kind) {
    case 'summary_or_report':
      // Reports is behind its module switch; with the module off the Reports page leads nowhere, so name the switch.
      return ctx?.reportsModuleOn === false
        ? { nextStep: 'For a summary or a digest, make a report instead. The Reports module is off for this workspace; turn it on under Settings → Modules', href: MODULES_HREF }
        : { nextStep: 'For a summary or a digest, make a report instead → Reports', href: REPORTS_HREF };
    case 'accept_or_decide':
      return {
        nextStep: 'Accepting is set on the value\'s AI setting: AI provides → Settings → Attributes',
        href: ctx?.valueKey ? `${ATTRIBUTES_HREF}/${ctx.valueKey}` : ATTRIBUTES_HREF,
      };
    case 'only_some_items':
      return {
        nextStep: `An agent works on any item it is asked about. To run it on ${ctx?.scope?.trim() || 'some items'} only, use Run now → Track.`,
      };
    case 'no_such_type':
      return { nextStep: `No agent type does this yet. The types are ${CUSTOMIZABLE_BEHAVIORS.map((b) => b.label.toLowerCase()).join(', ')}.` };
    case 'no_such_value':
      return { nextStep: 'This workspace has no value by that name. Add one under Settings → Attributes, or pick one below.', href: ATTRIBUTES_HREF };
    default:
      return { nextStep: `No agent type does this yet: ${asked}` };
  }
}

// ── The prompt ────────────────────────────────────────────────────────────────

function systemPrompt(ctx: AgentFromWordsContext): string {
  const types = CUSTOMIZABLE_BEHAVIORS.map(
    (b) => `- ${b.kind}: "${b.label}". ${b.summary}${b.examples.length ? ` Examples: ${b.examples.join('; ')}.` : ''}`,
  ).join('\n');
  const values = ctx.values.length
    ? ctx.values.map((v) => `- ${v.key}: ${v.label}`).join('\n')
    : '- (this workspace has no values with a scale yet)';
  return [
    'A person describes an agent they want for their workspace. You turn the description',
    'into a draft for the agent creator. An agent is one of these types and nothing else:',
    types,
    '',
    'Every agent of a type produces only what that type produces, reads only what that type',
    'may read, and a person always reviews what it produces. You cannot give it other outputs,',
    'other sources, other triggers, reviewers, automatic acceptance, a model or limits.',
    '',
    'The estimate type works on one of these workspace values (return its key as valueKey):',
    values,
    '',
    'Respond with JSON only, no markdown fence, matching exactly:',
    '{"kind": "<one of the type keys, or null if no type fits>",',
    ' "name": "<a short name for the agent, at most 5 words, plain words, no codename>",',
    ' "description": "<one line for its card: what it is for>",',
    ' "words": "<what the agent should know, in the person\'s own voice, as instructions it reads before it works — only what its type can act on>",',
    ' "valueKey": "<the value key for the estimate type, else null>",',
    ' "cannot": [{"kind": "<summary_or_report | accept_or_decide | only_some_items | other>", "asked": "<the part of the request, in their words>"}]}',
    '',
    'Rules for "cannot": a weekly summary, a digest, a report or a list to read is',
    'summary_or_report. Accepting, approving, setting or deciding values automatically is',
    'accept_or_decide. Working only on some items, one team, one track or one area is',
    'only_some_items (put the area in "asked"). Anything else no type does is other.',
    'Never keep any of these in "words". If the whole request is one of these, set kind to null.',
    'Do not ask a follow-up question; this is a one-shot draft.',
  ].join('\n');
}

// ── Parsing and the clamp ─────────────────────────────────────────────────────

interface RawCannot {
  kind?: unknown;
  asked?: unknown;
}

/** What the model may send. Anything else is ignored by the clamp. */
export interface RawAgentDraft {
  kind?: unknown;
  name?: unknown;
  description?: unknown;
  words?: unknown;
  valueKey?: unknown;
  cannot?: unknown;
  [extra: string]: unknown;
}

function extractJson(raw: string): RawAgentDraft | null {
  const trimmed = raw.trim().replace(/^```(?:json)?\s*/i, '').replace(/```\s*$/, '');
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed as RawAgentDraft;
  } catch {
    // not JSON
  }
  return null;
}

function text(v: unknown, max: number): string {
  return typeof v === 'string' ? v.replace(/\s+/g, ' ').trim().slice(0, max) : '';
}

/** The agent's words keep their line breaks (one per break); other whitespace is collapsed. */
function multilineText(v: unknown, max: number): string {
  return typeof v === 'string' ? v.replace(/[^\S\n]+/g, ' ').replace(/\s*\n\s*/g, '\n').trim().slice(0, max) : '';
}

/**
 * Sentences in the agent's words that ask for what no agent type can hold. A
 * sentence that matches is taken out of the words and listed as "could not
 * set", so the person sees it instead of the agent keeping it quietly.
 */
const CANNOT_IN_WORDS: { kind: CouldNotSetKind; test: RegExp }[] = [
  // A summary is asked for on a cadence or as something to send, write or post — not any sentence with the word in it
  // ("use the item's summary field" is an ordinary instruction).
  { kind: 'summary_or_report', test: /\b(weekly|daily|monthly|friday|monday)\b[^.]*\b(summary|digest|report|brief|list)\b|\b(send|email|mail|write|post|produce|give|compile)\b[^.]*\b(summary|summaries|digest|report|brief)\b/i },
  { kind: 'accept_or_decide', test: /\b(auto(matically|-)?\s*(accept|approve|set|apply|decide)|accept(s|ed)?\s+(it|them|the value|values|automatically|without)|without (a )?(review|person|human)|no (review|approval) needed|approve(s)? on its own|decide(s)? on its own)\b/i },
  // Narrowing to a team, track or area — "only work on Platform items" — not a sizing rule about some items
  // ("only the items with a billing tag are Large").
  { kind: 'only_some_items', test: /\b(only|just)\s+(on|for|work on|look at)\b[^.]*\b(items?|team|track|area|platform|product|pool)\b|\bignore (everything|all|items?) (else|outside|not in)\b/i },
];

/** The words as lines, each line as sentences, so a paragraph break the model sent is kept. */
function splitLines(words: string): string[][] {
  return words
    .split(/\n+/)
    .map((line) =>
      line
        .split(/(?<=[.!?])\s+/)
        .map((s) => s.trim())
        .filter(Boolean),
    )
    .filter((line) => line.length);
}

/**
 * The pure clamp. A draft is only {kind, name, description, words, valueKey};
 * it can narrow what the model sent, never widen it. Returns null for the draft
 * when no type fits, with the reasons.
 */
export function clampAgentDraft(raw: RawAgentDraft, ctx: AgentFromWordsContext): { draft: AgentDraft | null; couldNotSet: CouldNotSet[] } {
  const couldNotSet: CouldNotSet[] = [];
  const seen = new Set<string>();
  const add = (kind: CouldNotSetKind, asked: string, extra?: { valueKey?: string; scope?: string; reportsModuleOn?: boolean }) => {
    const key = `${kind}:${asked.toLowerCase()}`;
    if (seen.has(key)) return;
    seen.add(key);
    couldNotSet.push({ kind, asked, ...nextStepFor(kind, asked, extra) });
  };

  // What the model itself said it could not set.
  if (Array.isArray(raw.cannot)) {
    for (const c of raw.cannot as RawCannot[]) {
      const asked = text(c?.asked, 200);
      if (!asked) continue;
      const k = typeof c?.kind === 'string' ? c.kind : 'other';
      const kind: CouldNotSetKind =
        k === 'summary_or_report' || k === 'accept_or_decide' || k === 'only_some_items' ? k : 'other';
      add(kind, asked, { scope: asked, reportsModuleOn: ctx.reportsModuleOn });
    }
  }

  const kindRaw = typeof raw.kind === 'string' ? raw.kind : null;
  const kind = kindRaw && isAgentBehaviorKind(kindRaw) && AGENT_BEHAVIORS[kindRaw].customizable ? kindRaw : null;
  if (!kind) {
    if (kindRaw) add('no_such_type', kindRaw);
    else if (!couldNotSet.length) add('no_such_type', text(raw.description, 200) || 'what was asked');
    return { draft: null, couldNotSet };
  }

  // The words: take out any sentence that asks for what the agent cannot hold; the line breaks stay.
  const keptLines: string[] = [];
  for (const line of splitLines(multilineText(raw.words, WORDS_MAX))) {
    const kept: string[] = [];
    for (const sentence of line) {
      const hit = CANNOT_IN_WORDS.find((c) => c.test.test(sentence));
      if (hit) {
        const scope = sentence.match(/\b(?:only|just)\s+(?:on|for|work on|look at)\s+([^.,;]+)/i)?.[1];
        add(hit.kind, sentence, { scope, reportsModuleOn: ctx.reportsModuleOn });
      } else kept.push(sentence);
    }
    if (kept.length) keptLines.push(kept.join(' '));
  }

  let valueKey: string | undefined;
  if (kind === 'estimate_value') {
    const asked = text(raw.valueKey, 80);
    if (asked) {
      const found = ctx.values.find((v) => v.key === asked || v.label.toLowerCase() === asked.toLowerCase());
      if (found) valueKey = found.key;
      else add('no_such_value', asked);
    }
  }

  const name = text(raw.name, NAME_MAX) || AGENT_BEHAVIORS[kind].label;
  const draft: AgentDraft = {
    kind,
    name,
    description: text(raw.description, DESCRIPTION_MAX),
    words: keptLines.join('\n').trim(),
    ...(valueKey ? { valueKey } : {}),
  };
  return { draft, couldNotSet };
}

// ── The call ──────────────────────────────────────────────────────────────────

/**
 * One model call with one retry on a reply that is not JSON, then the clamp.
 * Never returns anything the clamp did not pass.
 */
export async function agentFromWords(
  words: string,
  ctx: AgentFromWordsContext,
  options: AgentFromWordsOptions = {},
): Promise<AgentFromWordsResult> {
  const callLlm = options.callLlm ?? callFeatureLlm;
  const checkEnabled = options.isEnabled ?? isAiCapabilityEnabled;

  const said = words.trim();
  if (!said) return { ok: false, reason: 'Say what it should do first.' };
  if (!checkEnabled(AGENT_FROM_WORDS_CAPABILITY_ID)) {
    return { ok: false, reason: 'Saying an agent is turned off for this workspace (Settings → AI Assists).' };
  }

  const system = systemPrompt(ctx);
  const conversationId = options.conversationId ?? `agent-from-words-${said.slice(0, 40)}`;
  let lastReply: string | null = null;

  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    const userPrompt =
      attempt === 1
        ? `The agent they want: ${said}`
        : `The agent they want: ${said}\n\nYour previous reply was not the JSON object asked for (it began "${lastReply}"). Reply again with only that JSON object.`;
    const raw = await callLlm({
      userPrompt,
      systemPrompt: system,
      conversationId,
      model: DEFAULT_MODEL,
      maxTokens: 700,
      temperature: 0.2,
    });
    if (!raw) return { ok: false, reason: 'AI is unavailable right now: no provider answered.' };
    const parsed = extractJson(raw);
    if (!parsed) {
      lastReply = raw.slice(0, 80);
      continue;
    }
    const { draft, couldNotSet } = clampAgentDraft(parsed, ctx);
    return { ok: true, draft, couldNotSet };
  }
  return { ok: false, reason: 'AI could not make a draft from that. Try saying it another way, or pick a type below.' };
}
